import { vec2 } from "@tlaukkan/tsm";
import { CellCategory as CellCategory, Speeds } from "./constants";
import { GameElement } from "./gameElementTypes";
import { cellLocations } from "./generated/assetLocations";
import { AssetLocation, degToRad, pixelsToMeters } from "./utils";

export type ElementStats = {
  health: number;
  range?: number;
  cooldown?: number;
  speed?: number;
  sellValue?: number;
  effectRadius?: number;
  charge?: number;
};

export type StatName = keyof ElementStats;

interface DisplayInfo {
  asset: string;
  displayName: string;
  stats: ElementStats;
  veterenStats?: Partial<ElementStats>;
  constants?: { [key: string]: number };
  veterenConstants?: { [key: string]: number };
  description: string;
  details?: string;
  veterenDescription?: string;
  category?: CellCategory;
  width?: number;
  height?: number;
  imageElements?: HTMLImageElement[];
  location?: AssetLocation;
}

//Note: When displayed underscores become spaces, and first letters are capitialized.
export const KeywordDisplayInfo = {
  "tag_protein": `A protein that freezes viruses for 1 second and makes them visible to T-Cells at twice the normal distance.`,
  "tagged": `A virus marked by a tag protein. Visible to T-Cells at twice the normal distance.`,
  "t-cell": "A category of immune cell that targets viruses. Includes Seekers and Turrets.",
  "b-cell": "A category of immune cell that often receives buffs. Includes Walls, Chains, and Wildcards.",
  "bond": "A physical link between cells. Attaches to the nearest immune cell.",
  "bonds": "Physical links between cells. Attaches to the nearest immune cell.",
  "bullet_protein": "A protein that damages viruses. Passes through friendly cells.",
  "virus_protein": "A protein that is dropped when a virus dies.",
  "magnetic_field": "A field that strongly attracts viruses within its range.",
  "carbs": "Currency used to buy immune cells.",
  "carb": "Currency used to buy immune cells.",
  "connected": "A network of cells connected through one or more bonds.",
  "eat": "Consume a protein within range. Some cells trigger a special ability when they eat a certain amount.",
  "eats": "Consume a protein within range. Some cells trigger a special ability when they eat a certain amount.",
  "elite": "A cell is elite when it has been combined twice. Has increased stats and sometimes unique abilities.",
  "electric_charge": "Will conduct across immune cells, proteins, and viruses, Kills viruses.",
  "draws_electric_charge": "Electric Charge will be drawn to this cell, if there are no viruses it can reach.",
  "explode": "Damage all viruses AND immune cells in range, and pushes everything away.",
  "leftover_carb": "Carbs that were not spent in the shop phase.",
  "leftover_carbs": "Carbs that were not spent in the shop phase.",
};

export const ElementDisplayInfo = new Map<GameElement, DisplayInfo>([
  // #region Immune Cells
  [
    GameElement.ORBITER,
    {
      asset: 'orbiter_new',
      displayName: 'Orbiter',
      stats: { health: 6, speed: Speeds.Normal },
      veterenStats: { speed: Speeds.Normal },
      description: `
		Orbits blood cells.
	`,
      details: `Always orbits counter-clockwise.`
    },
  ],

  [
    GameElement.SPINNER,
    {
      asset: 'spinner',
      displayName: 'Spinner',
      stats: { health: 6, speed: Speeds.Normal },
      veterenStats: { speed: Speeds.Fast },
      description: `
		Orbits attached cell.
	`,
    },
  ],

  [
    GameElement.SEEKER,
    {
      asset: 'seeker_2',
      displayName: 'Seeker',
      stats: { health: 2, range: 30, speed: 18 },
      veterenStats: { range: 40 },
      category: CellCategory.TCell,
      description: `
		Seeks viruses in range.
	`,
    },
  ],

  [
    GameElement.TAG_SEEKER_SPLITTER,
    {
      asset: 'tag_seeker',
      displayName: 'Tag Seeker Splitter',
      stats: { health: 2, range: 30, speed: 18 },
      veterenStats: { range: 40 },
      constants: { "tagAmount": 3 },
      category: CellCategory.TCell,
      description: `
      Seeks viruses and $tag_protein. $Eat $tagAmount tags to split in two. 
	`,
      details: `Will seek out tag protein if no viruses in range. Splitting resets health.`
    },
  ],

  [
    GameElement.MINELAYER,
    {
      asset: 'minelayer',
      displayName: 'Minelayer',
      stats: { health: 6, range: 30, speed: 18 },
      constants: { "dropDistance": 8 },
      veterenStats: { range: 40 },
      category: CellCategory.TCell,
      description: `
      Seeks viruses. Drops stationary bullets every $dropDistance microns.
	`,
    },
  ],

  [
    GameElement.TAG_PROOF_SEEKER,
    {
      asset: 'minelayer',
      displayName: 'Tag-Proof Seeker',
      stats: { health: 6, range: 30, speed: 18 },
      veterenStats: { range: 40 },
      category: CellCategory.TCell,
      description: `
      Seeks viruses. Immune to damage from $tagged viruses.
	`,
    },
  ],

  [
    GameElement.TAG_SPRAYER,
    {
      asset: 'sprayer',
      displayName: 'Tag Sprayer',
      stats: { health: 6, range: 60, cooldown: 0.05 },
      constants: { "tagAmount": 30 },
      veterenConstants: { "tagAmount": 60 },
      description: `Sprays $tag_protein in a 180 degree arc.`,
      details: `Up to $tagAmount proteins`
    },
  ],

  [
    GameElement.TAG_DRAG_DROPPER,
    {
      asset: 'dropper',
      displayName: 'Tag Drag Dropper',
      stats: { health: 8 },
      constants: { "tagAmount": 15, "dropDistance": 6 },
      veterenConstants: { "tagAmount": 30, "dropDistance": 6 },
      category: CellCategory.BCell,
      description: `Drops $tag_protein when you drag this cell around in the Shop.`,
      details: `Drops 1 $tag_protein every $dropDistance microns. Up to $tagAmount tags. Pick up again next turn to reset tags.`,
    },
  ],

  [
    GameElement.FAST_SEEKER,
    {
      asset: 'seeker',
      displayName: 'Fast Seeker',
      stats: { health: 5, range: 50, cooldown: 1.0 },
      veterenStats: { range: 75 },
      description: `
      Seeks viruses in range. Moves with a burst of speed.
	`,
    },
  ],

  [
    GameElement.MARTYR,
    {
      asset: 'plus',
      displayName: 'Health Bomb',
      stats: { health: 1, effectRadius: 25 },
      veterenStats: { effectRadius: 25 },
      constants: { "healAmount": 1 },
      veterenConstants: { "healAmount": 2 },
      description: `
      When this dies, give all immune cells in range +$healAmount health.
	`,
      veterenDescription: `
      When this dies, give all immune cells in range +$healAmount health.
	`,
    },
  ],

  [
    GameElement.HEALER,
    {
      asset: 'healer',
      displayName: 'Regen',
      stats: { health: 4, cooldown: 5, effectRadius: 15 },
      veterenStats: { cooldown: 2.5 },
      constants: { "healAmount": 1 },
      description: `
      Give all immune cells in range +$healAmount health every $cooldown seconds.`,
    },
  ],

  [
    GameElement.BCELL,
    {
      asset: 'bcell',
      displayName: 'Tag Dropper',
      stats: { health: 6, speed: Speeds.Normal, cooldown: 0.5 },
      veterenStats: { cooldown: 0.25 },
      description: `Moves forward and leaves a trail of $tag_protein.`,
      details: `Drops a $tag_protein every $cooldown seconds.`
    },
  ],

  [
    GameElement.CHAINER,
    {
      asset: 'chainer_new',
      displayName: 'Bond',
      stats: { health: 4 },
      category: CellCategory.BCell,
      constants: { "maxBonds": 2 },
      veterenConstants: { "maxBonds": 4 },
      description: `
		$bonds $maxBonds cells.
	`,
    },
  ],

  [
    GameElement.SWAPPER_CHAIN,
    {
      asset: 'chain_swap',
      displayName: 'Swapper Bond',
      stats: { health: 3 },
      constants: { "maxBonds": 1 },
      category: CellCategory.BCell,
      description: `
		$bonds to $maxBonds cell. At the start of your next shop round, swap health with the bonded cell.
	`,
    },
  ],

  [
    GameElement.INFINITE_CHAINER,
    {
      asset: 'infinite_chainer',
      displayName: 'Infinite Bond',
      stats: { health: 3 },
      constants: { "maxBonds": 1 },
      category: CellCategory.BCell,
      description: `$bonds to $maxBonds cell. Bonded cells can bond to an additional cell`,
    },
  ],

  [
    GameElement.PUSHER,
    {
      asset: 'pusher',
      displayName: 'Pusher',
      stats: { health: 1, cooldown: 1, effectRadius: 12 },
      veterenStats: { cooldown: 0.5 },
      description: `Pushes away everything around it every $cooldown second.`,
      veterenDescription: `Pushes away everything around it every $cooldown seconds.`,
    },
  ],

  [
    GameElement.BOMB,
    {
      asset: 'bomb',
      displayName: 'Bomb',
      stats: { health: 1, effectRadius: 15 },
      veterenStats: { effectRadius: 20 },
      description: `
		When this dies, $explode.
	`,
    },
  ],

  [
    GameElement.CONSUME_BOMB,
    {
      asset: 'Otherbomb',
      displayName: 'Hungry Bomb',
      stats: { health: 1, effectRadius: 10 },
      veterenStats: {},
      constants: { "consumeAmount": 1, "blastIncrement": 1 },
      veterenConstants: { "blastIncrement": 2 },
      description: `
    When this dies, $explode. $Eat $consumeAmount $virus_protein to increase blast radius.
	`,
      details: `Eat $consumeAmount to increase radius by $blastIncrement microns this battle.`
    },
  ],

  [
    GameElement.SHOOTER,
    {
      asset: 'shooter_new',
      displayName: 'Sniper',
      stats: { health: 2, cooldown: 1.5, range: 80 },
      veterenStats: { cooldown: 1 },
      description: `
		Shoots a $bullet_protein at a virus in line of sight.
	`,
    },
  ],

  [
    GameElement.AIM_SHOOTER,
    {
      asset: 'shooter',
      displayName: 'Turret',
      stats: { health: 1, range: 40, cooldown: 1.5 },
      veterenStats: { cooldown: 1.0 },
      category: CellCategory.TCell,
      description: `
      Shoots a $bullet_protein at the nearest virus in range.
	`,
      details: `Rotates to face target.`
    },
  ],

  [
    GameElement.BIG_SHOOTER,
    {
      asset: "big_bullet",
      displayName: "Resonant Cannon",
      stats: { health: 1, range: 40, cooldown: 1.5 },
      veterenStats: { cooldown: 1.25 },
      category: CellCategory.TCell,
      description: `
      Shoots a $bullet_protein at the nearest virus in range. Bullets are bigger for each $b-cell you own.
	`,
      details: `+1 health per B-Cell. More health can penetrate more viruses.`
    },
  ],

  [
    GameElement.WIND_SHOOTER,
    {
      asset: 'gatling',
      displayName: 'Gatling Gun',
      stats: { health: 4, range: 40, cooldown: 2 },
      veterenStats: { cooldown: 1.25 },
      constants: { "reduction": 0.25 },
      description: `
      Shoots $bullet_protein in a straight line. Shoots faster with each kill.
	`,
      details: `$reduction second cooldown reduction each kill.`
    },
  ],

  [
    GameElement.WALL,
    {
      asset: 'wall_new',
      displayName: 'Wall',
      stats: { health: 3 },
      category: CellCategory.BCell,
      description: `Wider than other cells.`,
    },
  ],

  [
    GameElement.THORN_WALL,
    {
      asset: 'Thorn_wall',
      displayName: 'Impact Wall',
      stats: { health: 6 },
      category: CellCategory.BCell,
      description: `
      When this takes damage, shoot 2 bullets from wall ends.
      `,
    },
  ],

  [
    GameElement.BULLET_WALL,
    {
      asset: 'bullet_wall',
      displayName: 'Bullet Wall',
      stats: { health: 6 },
      constants: { "consumeAmount": 1 },
      category: CellCategory.BCell,
      description: `
      $Eats $consumeAmount $bullet_protein, then shoots 2 bullets from wall ends.`,
      details: `Friendly shooters will try to fire at this.`
    },
  ],

  [
    GameElement.SHRAPNEL,
    {
      asset: 'claymore',
      displayName: 'Shrapnel',
      stats: { health: 1, effectRadius: 60 },
      description: `
      $Eats $bullet_protein. When this dies, shoot them all at nearby viruses.
	`,
    },
  ],

  [
    GameElement.SIMPLE,
    {
      asset: 'hammer_1',
      displayName: 'Tackle',
      stats: { health: 3, speed: Speeds.Fast },
      description: `Moves forward.`,
    },
  ],

  [
    GameElement.INCOME,
    {
      asset: 'coins_2',
      displayName: 'Bank',
      stats: { health: 1, sellValue: 1 },
      constants: { "incomeIncrement": 1 },
      veterenConstants: { "incomeIncrement": 2 },
      category: CellCategory.BCell,
      description: `Every time this survives battle, it sells for more $carbs. Currently sells for $sellValue.`,
      details: `Sell value does not decrease if killed.`
    },
  ],

  [
    GameElement.GROW_BOMB,
    {
      asset: 'Growbomb',
      displayName: 'Survivor Bomb',
      stats: { health: 1, effectRadius: 12 },
      constants: { "blastIncrement": 8 },
      description: `
      Each round this survives, increase its blast radius.
	`,
      details: `$blastIncrement micron increase per survived round. Increase is persistent.`
    },
  ],

  [
    GameElement.BUFFER_MAGNET,
    {
      asset: 'magnet_new',
      displayName: 'Magnet',
      stats: { health: 2, effectRadius: 20 },
      veterenStats: { effectRadius: 40 },
      description: `
		Has a $magnetic_field. 
	`,
    },
  ],

  [
    GameElement.KINETIC_BUFFER_MAGNET,
    {
      asset: "fist",
      displayName: "Kinetic Magnet",
      stats: { health: 6, effectRadius: 8 },
      constants: { "kineticIncrement": 2, "distance": 10 },
      veterenConstants: { "kineticIncrement": 3 },
      description: `Grows its $magnetic_field the further it is moved.`,
      details: `Grows $kineticIncrement microns every $distance microns moved. Cannot move on its own.`,
    },
  ],

  [
    GameElement.MONEY_WALL,
    {
      asset: 'money_wall',
      displayName: 'Greed Wall',
      stats: { health: 2 },
      constants: { "conversionRate": 2 },
      veterenConstants: { "conversionRate": 4 },
      category: CellCategory.BCell,
      description: `
      At the start of battle, gain $conversionRate health for each $leftover_carb.`,
    },
  ],

  [
    GameElement.INTEREST,
    {
      asset: 'Investment',
      displayName: 'Investment',
      stats: { health: 3 },
      constants: { "interestRate": 3 },
      veterenConstants: { "interestRate": 6 },
      category: CellCategory.BCell,
      description: `
      If you have unspent $carbs at the end of your shop turn, gain $interestRate carb.
      `,
    },
  ],

  [
    GameElement.MONEY_PRINTER,
    {
      asset: 'money_printer',
      displayName: 'Mint',
      stats: { health: 4 },
      constants: { "proteinAmount": 5, "incomeIncrement": 1 },
      veterenConstants: { "incomeIncrement": 2 },
      description: `
      $Eat $virus_protein to increase your $carbs.
      `,
      details: `Gain $incomeIncrement carb per $proteinAmount proteins. Gain carbs immediately.`
    },
  ],

  [
    GameElement.MONEY_BOMB_TURRET,
    {
      asset: 'money_bomb_cannon',
      displayName: 'Greed Cannon',
      stats: { health: 8, cooldown: 1, range: 40 },
      constants: { "carbCost": 2 },
      category: CellCategory.TCell,
      description: `
      Spends $carbCost $leftover_carbs to launch a Bomb at a virus within range.
      `,
      veterenDescription: `
      Spends $carbCost $leftover_carbs to launch an Elite Bomb at a virus within range.
      `
    },
  ],

  [
    GameElement.COLOR_GROW_WALL,
    {
      asset: 'inverse_wall',
      displayName: 'Resonant Wall',
      stats: { health: 3 },
      constants: { "healthIncrement": 1 },
      veterenConstants: { "healthIncrement": 2 },
      category: CellCategory.BCell,
      description: `
		When you buy a $b-cell, this gains +$healthIncrement health.
	`,
    },
  ],

  [
    GameElement.COLOR_BUFFER,
    {
      asset: 'buffer',
      displayName: 'Resonant Buffer',
      stats: { health: 1 },
      constants: { "healthIncrement": 1 },
      veterenConstants: { "healthIncrement": 2 },
      category: CellCategory.BCell,
      description: `
		When you buy a $b-cell, give it +$healthIncrement health.
	`,
    },
  ],

  [
    GameElement.COLOR_NEAREST_BUFFER,
    {
      asset: 'nearest_buffer',
      displayName: 'Proximity Buffer',
      stats: { health: 1 },
      constants: { "healthIncrement": 3 },
      category: CellCategory.BCell,
      description: `
		When you place this, give the nearest $b-cell +$healthIncrement health.
	`,
    },
  ],

  [
    GameElement.COLOR_BOOSTER,
    {
      asset: 'color_buffer',
      displayName: 'Resonance Amplifier',
      stats: { health: 1 },
      constants: { "healthIncrement": 1 },
      veterenConstants: { "healthIncrement": 2 },
      category: CellCategory.BCell,
      description: `
		When a $b-cell gains health, give it an additional +$healthIncrement health.
	`,
    },
  ],

  [
    GameElement.SEEKER_FACTORY,
    {
      asset: 'seeker_factory',
      displayName: 'Seeker Factory',
      stats: { health: 8 },
      constants: { "capsid": 1, "bullet": 2 },
      description: `
      $Eats $virus_protein and $bullet_protein to produce Seekers.
	`,
      veterenDescription: `
      $Eats $virus_protein and $bullet_protein to produce Elite Seekers.
	`,
      details: `Needs $capsid $virus_protein and $bullet $bullet_protein to activate. Friendly shooters will try to fire at this.`
    },
  ],

  [
    GameElement.COOLDOWN_ACCELLERATOR,
    {
      asset: 'cooldown',
      displayName: 'Accelerator Bond',
      stats: { health: 3 },
      constants: { "consume": 2, "seconds": 2, "maxBonds": 1 },
      category: CellCategory.BCell,
      description: `$bonds to $maxBonds cell. Bonded cell's cooldowns are decreased when this $Eats $consume $virus_protein.`,
      details: `Eats $consume $virus_protein to reduce cooldown by $seconds seconds.`
    },
  ],

  [
    GameElement.MORTAR,
    {
      asset: 'mortar',
      displayName: 'Mortar',
      stats: { health: 1 },
      constants: { "consume": 4 },
      description: `
        $Eats $bullet_protein. Eat $consume to launch a Bomb.
      `,
      veterenDescription: `
      $Eats $bullet_protein. Eat $consume to launch an Elite Bomb.
      `,
      details: `Friendly shooters will try to fire at this.`
    },
  ],

  [
    GameElement.KINETIC_CHAIN_BUFFER,
    {
      asset: 'Dumbbell',
      displayName: 'Kinetic Bond',
      stats: { health: 2 },
      constants: { "healthIncrement": 1, "distance": 20, "maxBonds": 2 },
      veterenConstants: { "healthIncrement": 2, "distance": 20, "maxBonds": 4 },
      category: CellCategory.BCell,
      description: `$bonds $maxBonds cells. Gives all $connected cells +$healthIncrement health the further it is moved.`,
      details: `+$healthIncrement health every $distance microns. Cannot move on its own.`
    },
  ],

  [
    GameElement.SPOTTER_CHAIN,
    {
      asset: 'Spotter',
      displayName: 'Radar Bond',
      stats: { health: 4 },
      constants: { "rangeIncrement": 20, "maxBonds": 2 },
      veterenConstants: { "rangeIncrement": 30, "maxBonds": 4 },
      category: CellCategory.BCell,
      description: `$bonds $maxBonds cells. Increases the range of all $connected cells.`,
      details: `Increase by $rangeIncrement microns.`
    },
  ],

  [
    GameElement.SCAPEGOAT_CHAIN,
    {
      asset: 'scapegoat',
      displayName: 'Bodyguard Bond',
      constants: { "maxBonds": 2 },
      veterenConstants: { "maxBonds": 4 },
      stats: { health: 7 },
      category: CellCategory.BCell,
      description: `$bonds $maxBonds cells. Damage done to $connected cells is done to this cell instead.`,
    },
  ],

  [
    GameElement.WILDCARD,
    {
      asset: 'wild',
      displayName: 'Wildcard',
      stats: { health: 4 },
      category: CellCategory.BCell,
      description: `
        This cell can be combined with ANY other non-elite cell.
      `,
    },
  ],

  [
    GameElement.SWAPPER,
    {
      asset: 'chain_swap',
      displayName: 'Swapper',
      stats: { health: 6, range: 30, speed: 18 },
      veterenStats: { speed: Speeds.Normal, range: 40 },
      description: `
        Has 1 $bonds. When this cell hits another immune cell, they swap health.
      `,
    },
  ],

  [
    GameElement.ELECTRIC,
    {
      asset: 'electric',
      displayName: 'Virus Zapper',
      stats: { health: 2, charge: 2 },
      veterenStats: { charge: 6 },
      description: `
        Has $charge $electric_charge.
      `,
    },
  ],

  [
    GameElement.ELECTRIC_GENERATOR_WALL,
    {
      asset: 'electric_wall',
      displayName: 'Electric Wall',
      stats: { health: 6, cooldown: 0.1 },
      category: CellCategory.BCell,
      constants: { "chargeIncrement": 1 },
      veterenConstants: { "chargeIncrement": 2 },
      description: `
        Gains $electric_charge when immune cells or viruses touch this cell.
      `,
      details: `$chargeIncrement charge per touch.`
    },
  ],

  [
    GameElement.ELECTRIC_KINETIC_GENERATOR,
    {
      asset: "electric_kinetic",
      displayName: "Kinetic Generator",
      stats: { health: 6 },
      constants: { "kineticIncrement": 1, "distance": 12 },
      veterenConstants: { "kineticIncrement": 2 },
      description: `
      Gains more $electric_charge the further it is moved.
	`,
      details: `$kineticIncrement charge per $distance microns. Cannot move on its own.`
    },
  ],

  [
    GameElement.ELECTROMAGNET,
    {
      asset: "electromagnet",
      displayName: "Electromagnet",
      stats: { health: 6 },
      constants: { "conversionRate": 5 },
      veterenConstants: { "conversionRate": 10 },
      description: `
		  $draws_electric_charge. The more $electric_charge it has, the larger its $magnetic_field.
	`,
      details: `Field grows $conversionRate microns per charge.`
    },
  ],
  [
    GameElement.ELECTRIC_HUNGRY_GENERATOR,
    {
      asset: 'electric_hungry',
      displayName: 'Electric Generator',
      stats: { health: 4 },
      constants: { "proteinAmount": 8, "increment": 6 },
      veterenConstants: { "proteinAmount": 10, "increment": 10 },
      description: `
      $Eat $virus_protein to gain $electric_charge.
      `,
      details: `Gains $increment $electric_charge per $proteinAmount proteins`
    },
  ],

  [
    GameElement.TAG_ORBITER,
    {
      asset: 'tag_orbiter',
      displayName: 'Tag Orbiter',
      stats: { health: 6, speed: Speeds.Normal, cooldown: 0.5 },
      veterenStats: { speed: Speeds.Normal, cooldown: 0.25 },
      description: `
      Orbits blood cells. Leaves a trail of $tag_protein.
      `,
      details: `Drops a $tag_protein every $cooldown seconds.`
    },
  ],

  [
    GameElement.PIERCE_SHOOTER,
    {
      asset: 'pierce_shooter',
      displayName: 'Pierce Turret',
      stats: { health: 1, range: 40, cooldown: 1.5 },
      veterenStats: { cooldown: 1.0 },
      constants: { "shotHealth": 2 },
      veterenConstants: { "shotHealth": 3 },
      category: CellCategory.TCell,
      description: `
      Shoots a $bullet_protein at a virus in line of sight. $bullet_protein from this cell are more powerful.
      `,
      details: `Bullets have $shotHealth health. More health can penetrate more viruses.`
    },
  ],

  [
    GameElement.HEAVY_CHAINER,
    {
      asset: 'heavy_chain',
      displayName: 'Anchor Chain',
      stats: { health: 8 },
      category: CellCategory.BCell,
      constants: { "maxBonds": 3 },
      veterenConstants: { "maxBonds": 6 },
      description: `
        $bonds $maxBonds cells. Weighs down bonded cells.
      `
    },
  ],
  [
    GameElement.STEM_CELL,
    {
      asset: 'oblong',
      displayName: 'Stem Cell',
      stats: { health: 1 },
      category: CellCategory.None,
      constants: { "range": 200 },
      description: `
          At the start of combat, transforms into the first immune cell in line of sight.
        `
    },
  ],
  [
    GameElement.SENTRY_ORBITER,
    {
      asset: 'orbiter_sentry',
      displayName: 'Sentry',
      stats: { health: 6, speed: Speeds.Normal, range: 40 },
      veterenStats: { speed: Speeds.Normal },
      description: `
		Orbits blood cells until virus detected, then blocks tries to block the virus.
	`,
      details: `Always orbits counter-clockwise. Stays on orbital path. Only sees virus outside of orbit radius.`
    },
  ],

  // #region Virus

  [
    GameElement.VIRUS,
    {
      asset: 'virus',
      displayName: 'Virus',
      stats: { health: 1 },
      description: `Tries to infect blood cells.`, //TODO: Need general term for blood cells later.
    },
  ],

  [
    GameElement.AOE_VIRUS,
    {
      asset: 'algae',
      displayName: 'Pyric Virus',
      stats: { health: 1, effectRadius: 12 },
      description: `When this dies, damages nearby immune cells.`,
    },
  ],

  [
    GameElement.VIROPHAGE,
    {
      asset: 'stack',
      displayName: 'Hungry Virus',
      stats: { health: 1 },
      description: `$Eats dropped $virus_protein to get more health.`,
      details: `$Eats (1) virus protein to gain +1 health.`,
    },
  ],

  [
    GameElement.ANTIBODY_SEEKER,
    {
      asset: 'fighter',
      displayName: 'Seeker Virus',
      stats: { health: 2 },
      description: `
		$Seeks immune cells in range if it can.
	`,
    },
  ],

  [
    GameElement.SPLITTER,
    {
      asset: 'bulb',
      displayName: 'Splitter Virus',
      stats: { health: 1 },
      description: `
		Splits into two fast moving viruses when near a blood cell!
	`,
    },
  ],

  [
    GameElement.JUMPER,
    {
      asset: 'jumper',
      displayName: 'Jumper Virus',
      stats: { health: 1 },
      description: `
		Moves slowly but then jumps quickly ahead. Invulnerable while jumping.
	`,
    },
  ],

  [
    GameElement.AVOIDER,
    {
      asset: 'needle',
      displayName: 'Avoider Virus',
      stats: { health: 1 },
      description: `
		Moves to avoid nearby immune cells.
  `,
    },
  ],

  [
    GameElement.WAVE,
    {
      asset: 'sine',
      displayName: 'Wave Virus',
      stats: { health: 1 },
      description: `Moves in a wave pattern.`,
    },
  ],

  [
    GameElement.VIRUS_PUSHER,
    {
      asset: 'bacteriophage',
      displayName: 'Pusher Virus',
      stats: { health: 1 },
      description: `
		When this dies, push away nearby immune cells.
	`,
    },
  ],
  [
    GameElement.SWARMER,
    {
      asset: 'spike',
      displayName: 'Swarmer Virus',
      stats: { health: 1 },
      description: `Attract and attach to other viruses.`,
    },
  ],


]);

export function getCellCategory(type: GameElement) {
  const info = ElementDisplayInfo.get(type);
  return info ? info.category : CellCategory.None;
}

export function getGameElementSizeMeters(type: GameElement) {
  const info = ElementDisplayInfo.get(type);
  const asset = info ? info.asset : "bloodcell";
  const assetLoc = cellLocations[asset] as AssetLocation;
  return new vec2([
    pixelsToMeters(assetLoc.width),
    pixelsToMeters(assetLoc.height),
  ]);
}

